import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, of, shareReplay, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AS4QueryParams } from '../_model/as4-query-params';
import { OotsRequest } from '../_model/oots-request';
import { EvidenceSave } from '../_model/evidence';

const OOTS_API_URL = environment.api_url + "/oots";

const headers = new HttpHeaders({
  "Content-Type": "application/json",
  "Access-Control-Allow-Origin": environment.api_url,
  "Access-Control-Allow-Headers": "Origin, Content-Type, X-Auth-Token, Accept, Authorization, X-Request-With, Access-Control-Request-Method, Access-Control-Request-Headers",
  "Access-Control-Allow-Credentials": "true",
  "Access-Control-Allow-Methods": "GET, POST, DELETE, PUT, OPTIONS, TRACE, PATCH, CONNECT",
  "Access-Control-Max-Age": "300"
});

@Injectable({
  providedIn: 'root'
})
export class OotsService {

  private versionCache: { app: string; tdd: string } | null = null;
  private versionRequest$: Observable<{ app: string; tdd: string }> | null = null;
  
  constructor(private http: HttpClient) { }

  resetOOTSRequest(uuid: string): Observable<any> {
    const request = new OotsRequest();
    request.uuid = uuid;
    return this.http.post<any>(OOTS_API_URL + "/request/reset", request, { headers })
      .pipe(
        catchError(this.handleError)
      )
  }

  setUserIdentifier(ootsRequest: OotsRequest): Observable<any> {
    return this.http.post<any>(OOTS_API_URL + "/user", ootsRequest, { headers })
      .pipe(
        catchError(this.handleError)
      )
  }

  sendPlainXML(xml: string): Observable<any> {
    return this.http.post<any>(OOTS_API_URL + "/request", xml, { headers, responseType: 'text' as 'json' })
      .pipe(
        catchError(this.handleError)
      )
  }

  getOOTSRequestByUUID(uuid: string, lang: string): Observable<any> {
    const httpParams = new HttpParams().append("uuid", uuid).append("lang", lang);
    const httpOptions = {
      headers: headers,
      params: httpParams,
      responseType: 'text' as 'json'
    };

    return this.http.get<any>(OOTS_API_URL + "/request", httpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  getVersion(): Observable<any> {
    if (this.versionCache) return of(this.versionCache);
    if (this.versionRequest$) return this.versionRequest$;
    this.versionRequest$ = this.http.get<any>(OOTS_API_URL + "/version", { headers }).pipe(
      tap(response => this.versionCache = response), shareReplay(1)
    );
    return this.versionRequest$;
  }

  findPreviousRequestByToken(userIdentifier: string, continuationToken: string): Observable<any> {
    const httpParams = new HttpParams()
      .append("userIdentifier", userIdentifier)
      .append("continuationToken", continuationToken);

    const httpOptions = {
      headers: headers,
      params: httpParams,
      responseType: 'text' as 'json'
    };

    return this.http.get<any>(OOTS_API_URL + "/find", httpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  getDocuments(uuid: string, action: string, email: string, phoneNumber: string): Observable<any> {
    let httpParams = new HttpParams().append("uuid", uuid);
    if (action != null) httpParams = httpParams.append("action", action);
    if (email != null) httpParams = httpParams.append("email", email);
    if (phoneNumber != null) httpParams = httpParams.append("phoneNumber", phoneNumber);
    httpParams = httpParams.append("language", "es");
    const httpOptions = {
      headers: headers,
      params: httpParams,
      responseType: 'text' as 'json'
    };

    return this.http.get<any>(OOTS_API_URL + "/response", httpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  cancelEvidence(evidenceUUID: string): Observable<any> {
    return this.http.post<any>(OOTS_API_URL + "/request/evidence/cancel", evidenceUUID, { headers })
      .pipe(
        catchError(this.handleError)
      )
  }

  deleteEvidence(evidenceUUID: string): Observable<any> {
    return this.http.post<any>(OOTS_API_URL + "/request/evidence/delete", evidenceUUID, { headers })
      .pipe(
        catchError(this.handleError)
      )
  }

  getRequirement(ootsRequestUUID: string, requirementId: string): Observable<any> {
    let httpParams = new HttpParams();
    if (ootsRequestUUID != null) httpParams = httpParams.append("uuid", ootsRequestUUID);
    if (requirementId != null) httpParams = httpParams.append("requirementId", requirementId);
    const httpOptions = { headers: headers, params: httpParams }
    return this.http.get<any>(OOTS_API_URL + "/request/requirement", httpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  as4Search(params: AS4QueryParams): Observable<any> {
    let httpParams = new HttpParams();
    if (params.requestId != null) httpParams = httpParams.append("requestId", params.requestId);
    if (params.conversationId != null) httpParams = httpParams.append("conversationId", params.conversationId);
    if (params.fromParty != null) httpParams = httpParams.append("fromParty", params.fromParty);
    if (params.toParty != null) httpParams = httpParams.append("toParty", params.toParty);
    if (params.dateFrom != null) httpParams = httpParams.append("dateFrom", params.dateFrom);
    if (params.dateTo != null) httpParams = httpParams.append("dateTo", params.dateTo);
    const httpOptions = { headers: headers, params: httpParams }
    return this.http.get(OOTS_API_URL + "/search", httpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  as4Download(requestId: string, type: string): Observable<any> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append("type", type);
    const httpOptions = { headers: headers, params: httpParams }
    return this.http.get(OOTS_API_URL + "/search/" + requestId, httpOptions)
      .pipe(
        catchError(this.handleError)
      )
  }

  handleError(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) errorMessage = error.error.message;
    else errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    console.log(errorMessage);
    return throwError(() => { return errorMessage; });
  }

  saveEvidences(evidences: EvidenceSave[], uuid: string){
    return this.http.post<any>(OOTS_API_URL + "/saveEvidences?uuid=" + uuid, evidences)
    .pipe(
      catchError(this.handleError)
    )
  }
}
